import { useMemo, useState, type ChangeEvent } from 'react';
import { Button, CoordioDropdown } from '@coordio/ui';
import { FileUp as UploadFileIcon } from 'lucide-react';
import type { FileCategory } from '../../types/file';
import { fileService } from '../../services/fileService';
import { useNotificationStore } from '../../store/notificationStore';

export interface FileUploadProps {
  sessionId: string;
  onUploaded?: () => void;
}

type UploadItem = {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
};

const categoryLabel = (c: FileCategory) => {
  switch (c) {
    case 0:
      return 'Input';
    case 1:
      return 'Output';
    case 2:
      return 'Generated';
    case 3:
      return 'Attachment';
    case 4:
      return 'Temporary';
    case 5:
      return 'Artifact';
    case 6:
      return 'Log';
    default:
      return 'Unknown';
  }
};

/**
 * Upload component for session files with drag-and-drop and progress.
 */
export const FileUpload = ({ sessionId, onUploaded }: FileUploadProps) => {
  const { addNotification } = useNotificationStore();

  const [category, setCategory] = useState<FileCategory>(0);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [uploading, setUploading] = useState(false);

  const canUpload = useMemo(() => items.some((i) => i.status === 'pending') && !uploading, [items, uploading]);

  const enqueueFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setItems((prev) => [
      ...prev,
      ...arr.map((f) => ({
        file: f,
        progress: 0,
        status: 'pending' as const,
      })),
    ]);
  };

  const handleSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    enqueueFiles(e.target.files);
    e.target.value = '';
  };

  const startUpload = async () => {
    setUploading(true);
    try {
      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        if (item.status !== 'pending') continue;

        setItems((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'uploading', progress: 0, error: undefined };
          return next;
        });

        try {
          await fileService.uploadFile(sessionId, item.file, category, {
            onProgress: (p) => {
              setItems((prev) => {
                const next = [...prev];
                if (!next[idx]) return prev;
                next[idx] = { ...next[idx], progress: p };
                return next;
              });
            },
          });

          setItems((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], status: 'done', progress: 100 };
            return next;
          });

          onUploaded?.();
        } catch (err: any) {
          const msg = err.response?.data?.message || err.message || 'Upload failed';
          setItems((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], status: 'error', error: msg };
            return next;
          });
          addNotification(msg, 'error');
        }
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h6 className="text-sm font-medium mb-1">Files</h6>

      <div className="mb-2">
        <label className="mb-1 block text-sm font-medium" id="file-category-label">Category</label>
        <CoordioDropdown
          options={[0, 1, 2, 3, 4, 5, 6].map((c) => ({
            value: String(c),
            label: categoryLabel(c as FileCategory),
          }))}
          value={String(category)}
          onChange={(val) => setCategory(Number(val ?? 0) as FileCategory)}
          disabled={uploading}
          clearable={false}
          size="sm"
          data-testid="file-category-select"
        />
      </div>

      <div
        className="border border-dashed border-border rounded p-1.5 text-center bg-card"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          enqueueFiles(e.dataTransfer.files);
        }}
      >
        <p className="text-sm text-muted-foreground mb-1">
          Drag & drop files here, or choose files
        </p>

        <Button variant="outline" asChild disabled={uploading}>
          <label className="cursor-pointer">
            <UploadFileIcon className="mr-2 h-4 w-4" />
            Choose files
            <input type="file" hidden multiple onChange={handleSelectFiles} />
          </label>
        </Button>

        <div className="mt-1 flex justify-center">
          <Button onClick={startUpload} disabled={!canUpload}>
            Upload
          </Button>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-1">
          {items.slice(0, 5).map((it, i) => (
            <div key={`${it.file.name}-${i}`} className="mb-1">
              <span className="text-xs text-muted-foreground">
                {it.file.name} ({Math.round(it.file.size / 1024)} KB) — {it.status}
                {it.status === 'error' && it.error ? `: ${it.error}` : ''}
              </span>
              <div className="w-full bg-muted rounded-full h-1.5 mt-0.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${it.status === 'error' ? 'bg-red-500' : 'bg-accent'}`}
                  style={{ width: `${it.progress}%` }}
                />
              </div>
            </div>
          ))}
          {items.length > 5 && (
            <span className="text-xs text-muted-foreground">
              Showing 5 of {items.length} files.
            </span>
          )}
        </div>
      )}
    </div>
  );
};
